import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useNav } from '../../context/NavContext';

/**
 * HamburgerMenu component for displaying additional navigation links in a dropdown
 * Contains less prominent pages like Apparel, Gift Cards, Careers, and Nutritional Info
 * 
 * @returns {JSX.Element} HamburgerMenu component
 */
export default function HamburgerMenu() {
  const menuRef = useRef(null);
  const { isDropdownActive, toggleDropdown, closeDropdown } = useNav();
  
  const isOpen = isDropdownActive('hamburger');
  
  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeDropdown('hamburger');
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeDropdown]);

  const menuItems = [
    {
      name: 'ABOUT US',
      href: '/about'
    },
    {
      name: 'GIFT CARDS',
      href: '/gift-cards'
    },
    {
      name: 'CAREERS',
      href: 'https://bte-management-company.r365hire.com/account',
      external: true
    },
    {
      name: 'APPAREL',
      href: 'https://iloveoph.gearnw.com/index_products.php',
      external: true
    },
    {
      name: 'NUTRITIONAL INFO',
      href: '/oph-nutritional-info.pdf',
      external: true
    }
  ];
  
  return (
    <div className="relative" ref={menuRef}>
      {/* Hamburger Menu Trigger */}
      <button 
        className="text-brand-navy hover:text-brand-orange transition-colors flex items-center"
        onClick={() => toggleDropdown('hamburger')}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="More options"
        onMouseEnter={() => toggleDropdown('hamburger')}
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className="h-6 w-6" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
      
      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className="absolute -left-8 mt-6 bg-brand-cream border border-brand-navy rounded-lg shadow-xl z-50 w-64"
          onMouseLeave={() => closeDropdown('hamburger')}
        >
          {/* Menu Items */}
          <div className="py-2">
            {menuItems.map((item, index) => (
              item.external ? (
                <a
                  key={index}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-4 py-3 text-brand-navy hover:bg-brand-navy hover:bg-opacity-5 hover:text-brand-orange transition-colors group"
                  onClick={() => closeDropdown('hamburger')}
                >
                  <span className="text-sm font-medium">{item.name}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-auto opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              ) : (
                <Link
                  key={index}
                  to={item.href}
                  className="flex items-center px-4 py-3 text-brand-navy hover:bg-brand-navy hover:bg-opacity-5 hover:text-brand-orange transition-colors group"
                  onClick={() => closeDropdown('hamburger')}
                >
                  <span className="text-sm font-medium">{item.name}</span>
                </Link>
              )
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

